import * as React from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import AddIcon from "@mui/icons-material/Add";

import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Close";
import {
  GridRowModesModel,
  GridRowModes,
  DataGrid,
  GridColDef,
  GridToolbarContainer,
  GridActionsCellItem,
  GridEventListener,
  GridRowId,
  GridRowModel,
  GridRowEditStopReasons,
  GridToolbarProps,
} from "@mui/x-data-grid";

import { ServiceItemType } from "src/interface/enum/ServiceItemType";
import { ISaleFunnelItem } from "src/interface/ISaleFunnelItem";
import {
  renderRatingEditInputCell,
  renderTime,
} from "./time-picker/TimePickerEditInputCell";

interface CustomToolbarProps extends GridToolbarProps {
  setRows: (newRows: (oldRows: ISaleFunnelItem[]) => ISaleFunnelItem[]) => void;
  setRowModesModel: (
    newModel: (oldModel: GridRowModesModel) => GridRowModesModel
  ) => void;

  rows: ISaleFunnelItem[];
  serviceItemType: any;
}

function EditToolbar(props: CustomToolbarProps) {
  const { setRows, setRowModesModel, rows } = props;
  const handleClick = () => {
    const ID = rows.length + 1; // ایجاد یک شناسه منحصر به فرد برای ردیف جدید
    const newRow: ISaleFunnelItem = {
      id: ID,
      daysAfterRegistration: 0,
      rowId: ID,
      saleFunnelRef: 0,
      smSText: "",
      timeSendSMS: "",
      isNew: true, // نشان‌دهنده ردیف جدید
    };

    setRows((oldRows) => [...oldRows, newRow]);

    setRowModesModel((oldModel) => ({
      ...oldModel,
      [ID]: { mode: GridRowModes.Edit, fieldToFocus: "title" },
    }));
  };
  return (
    <GridToolbarContainer>
      <Button color="primary" startIcon={<AddIcon />} onClick={handleClick}>
        آیتم جدید
      </Button>
    </GridToolbarContainer>
  );
}

export default function SalesFunnelItem({
  rows,
  setRows,
}: {
  rows?: any;
  setRows: any;
}) {
  const [rowModesModel, setRowModesModel] = React.useState<GridRowModesModel>(
    {}
  );

  const handleRowEditStop: GridEventListener<"rowEditStop"> = (
    params,
    event
  ) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      event.defaultMuiPrevented = true;
    }
  };

  const handleEditClick = (id: GridRowId) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
  };

  const handleSaveClick = (id: GridRowId) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
  };
  const handleDeleteClick = (id: GridRowId) => () => {
    setRows(rows?.filter((row: any) => row.id !== id));
  };

  const handleCancelClick = (id: GridRowId) => () => {
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: GridRowModes.View, ignoreModifications: true },
    });

    const editedRow = rows?.find((row: any) => row.id === id);
    if (editedRow?.isNew) {
      setRows(rows?.filter((row: any) => row.id !== id)); // حذف ردیف جدید در صورت انصراف
    }
  };

  const processRowUpdate = (newRow: GridRowModel) => {
    const updatedRow = { ...newRow, isNew: false }; // علامت‌گذاری ردیف به عنوان "نه جدید"

    setRows((oldRows: any) =>
      oldRows?.map((row: any) =>
        row.id === newRow.id ? (updatedRow as ISaleFunnelItem) : row
      )
    );

    return updatedRow;
  };
  const handleRowModesModelChange = (newRowModesModel: GridRowModesModel) => {
    setRowModesModel(newRowModesModel);
  };

  const columns: GridColDef[] = [
    {
      field: "rowId",
      headerName: "مرحله",
      minWidth: 100,
      filterable: true,
      align: "left",
      headerAlign: "left",
      flex: 1,
      editable: false,
      sortable: true,
    },
    {
      field: "smSText",
      headerName: "متن پیامک",
      minWidth: 350,
      filterable: true,
      align: "left",
      headerAlign: "left",
      flex: 3,
      editable: true,
      sortable: true,
    },
    {
      field: "daysAfterRegistration",
      headerName: "چند روز پس از ثبت شماره تماس ارسال شود؟",
      minWidth: 300,
      filterable: true,
      align: "left",
      headerAlign: "left",
      flex: 2,
      editable: true,
      sortable: true,
      type: "number",
    },
    {
      field: "timeSendSMS",
      headerName: "ساعت",
      minWidth: 150,
      filterable: true,
      align: "left",
      headerAlign: "left",
      flex: 2,
      editable: true,
      sortable: true,
      type: "string",
      renderEditCell: renderRatingEditInputCell,
      renderCell: renderTime,
    },

    {
      field: "actions",
      type: "actions",
      headerName: "عملیات",
      minWidth: 150,
      cellClassName: "actions",
      getActions: ({ id }) => {
        const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;
        if (isInEditMode) {
          return [
            <GridActionsCellItem
              icon={<SaveIcon />}
              label="Save"
              sx={{
                color: "primary.main",
              }}
              onClick={handleSaveClick(id)}
            />,
            <GridActionsCellItem
              icon={<CancelIcon />}
              label="انصراف"
              className="textPrimary"
              onClick={handleCancelClick(id)}
              color="inherit"
            />,
          ];
        }

        return [
          <GridActionsCellItem
            icon={<EditIcon />}
            label="ویرایش"
            className="textPrimary"
            onClick={handleEditClick(id)}
            color="inherit"
          />,
          <GridActionsCellItem
            icon={<DeleteIcon />}
            label="حذف"
            onClick={handleDeleteClick(id)}
            color="inherit"
          />,
        ];
      },
    },
  ];

  return (
    <Box>
      <DataGrid
        rows={rows}
        columns={columns}
        editMode="row"
        rowModesModel={rowModesModel}
        onRowModesModelChange={handleRowModesModelChange}
        onRowEditStop={handleRowEditStop}
        processRowUpdate={processRowUpdate}
        //@ts-ignore
        slots={{ toolbar: EditToolbar }}
        slotProps={{
          //@ts-ignore
          toolbar: {
            setRows,
            setRowModesModel,
            rows,
          },
        }}
      />
    </Box>
  );
}
